import clsx from 'clsx';
import { useTranslation } from 'hooks/useTranslation';
import { formatNumber } from 'utils/number';
import type { Theme } from './qunoscoreData';

type Props = {
  label?: string;
  value: boolean | string | number;
  min?: number;
  max?: number;
  theme?: Theme;
  formatYear?: boolean;
  keys?: Record<string, string | undefined>;
  isNotAvailable?: boolean;
};

const QunoscoreDetail = ({
  label,
  value,
  min,
  max,
  theme,
  formatYear = false,
  keys,
  isNotAvailable,
}: Props): JSX.Element => {
  const bg: Record<Theme, string> = {
    excellent: 'bg-light-green',
    acceptable: 'bg-secondary',
    insufficient: 'bg-light-red',
  };

  const { locale, t } = useTranslation();

  const valueToString = (
    value: string | number | boolean,
    formatYear: boolean,
    keys: Record<string, string | undefined> | undefined,
    locale: string,
  ): string => {
    if (formatYear && typeof value === 'number') {
      return `${new Date().getFullYear() - value}`;
    }

    if (typeof value === 'boolean') {
      return value
        ? t('doctorProfile.qunoscoreSection.yes')
        : t('doctorProfile.qunoscoreSection.no');
    }

    if (keys && value) {
      const key = keys[`${value}`];
      if (key) {
        return t(key);
      }
    }

    if (typeof value === 'number') {
      return formatNumber(value, locale);
    }

    return value;
  };

  return (
    <div className="flex justify-between mb-2">
      <p className="w-3/5 md:w-full">{label}</p>
      <div className="w-2/6 md:w-full text-right">
        {isNotAvailable ? (
          <>
            <span>n/a</span>
            <span className="inline-block w-16 md:w-20 text-center rounded-lg ml-3" />
          </>
        ) : (
          <>
            <span>{valueToString(value, formatYear, keys, locale)}</span>
            <span
              className={clsx(
                theme && bg[theme],
                theme === 'insufficient' && 'text-white',
                'inline-block w-16 md:w-20 text-center rounded-lg ml-3',
              )}
            >
              {t('doctorProfile.qunoscoreSection.outOf', {
                min,
                max,
              })}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default QunoscoreDetail;
